import React from "react";
import { useEffect } from "react";
import { useSelector, useDispatch } from "types/store";
import { isEnabled } from "utils/featureFlags";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import * as viewReducer from "containers/View/reducer";
import Select, { components } from "react-select";
import styles from "./style.module.css";
import { fetchAllSandboxes, getSandboxes } from "@/containers/OpenClaw/openClawSlice";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
const OpenShellSandboxSelector = ({}) => {
    const dispatch = useDispatch();
    const chatType = useSelector((state) => chatGPTReducer.getChatType(state));
    const sandboxes = useSelector((state) => getSandboxes(state));
    const isSandboxesLoading = useSelector((state) => viewReducer.getIsSandboxesLoading(state));
    const isOpenShellChat = isEnabled("openshell") && chatType === "OPENSHELL";
    const sandboxOptions = [...(sandboxes ?? [])]
        .sort((a, b) => {
        const aIsHealthy = a?.gateway_healthy;
        const bIsHealthy = b?.gateway_healthy;
        if (aIsHealthy && !bIsHealthy) {
            return -1;
        }
        else if (!aIsHealthy && bIsHealthy) {
            return 1;
        }
        return 0;
    })
        .map((sb) => {
        return {
            label: sb?.name,
            value: sb,
            isHealthy: sb?.gateway_healthy
        };
    });
    const selectedSandbox = useSelector((state) => chatGPTReducer.getSelectedSandbox(state));
    const selectedSandboxOption = (sandboxOptions ?? []).find((option) => option?.value?.name === selectedSandbox?.name);
    useEffect(() => {
        const missingSelectedSandbox = selectedSandbox && !selectedSandboxOption;
        if (missingSelectedSandbox && !isSandboxesLoading && isOpenShellChat) {
            dispatch(fetchAllSandboxes());
        }
    }, [selectedSandbox]);
    return (React.createElement("div", { className: "flex items-center w-[100%]" },
        React.createElement("div", { className: "mr-[8px]" }, "Sandbox: "),
        React.createElement("div", { className: "flex items-center w-[100%]" }, isSandboxesLoading ? (React.createElement(React.Fragment, null,
            React.createElement(LoadingIndicator, { style: { scale: "0.4", margin: "0", width: "48px", marginTop: "-2px" } }),
            React.createElement("div", null, "Loading Sandboxes"))) : (React.createElement(Select, { options: sandboxOptions, value: selectedSandboxOption, onChange: (sbOption) => {
                if (sbOption && "value" in sbOption && sbOption.value) {
                    dispatch(chatGPTReducer.initOpenshellAgent(sbOption?.value));
                }
            }, className: styles.sandboxSelector, components: {
                Option: CustomOption,
                SingleValue: CustomSingleValue,
                ValueContainer: CustomValueContainer
            }, isOptionDisabled: (option) => !option?.value?.gateway_healthy, placeholder: "Select a sandbox..." })))));
};
const ColoredDot = ({ status, styleOverride }) => {
    const getColor = () => {
        if (status === "healthy") {
            return "green";
        }
        return "red";
    };
    return (React.createElement("span", { style: {
            display: "inline-block",
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: getColor(),
            marginRight: 8,
            ...(styleOverride ?? {})
        } }));
};
const CustomOption = (props) => {
    const { isHealthy, label } = props.data;
    return (React.createElement(components.Option, { ...props },
        React.createElement("div", { className: "flex items-center" },
            React.createElement(ColoredDot, { status: isHealthy ? "healthy" : "unhealthy" }),
            React.createElement("div", { className: styles.sandboxSelectorOptionLabel }, label))));
};
const CustomSingleValue = (props) => {
    const { isHealthy, label } = props.data;
    return (React.createElement(components.SingleValue, { ...props },
        React.createElement("div", { className: "flex items-center" },
            React.createElement(ColoredDot, { status: isHealthy ? "healthy" : "unhealthy" }),
            React.createElement("div", { className: styles.sandboxSelectorSingleValueLabel }, label))));
};
const CustomValueContainer = (props) => {
    const selected = props?.selectProps?.value;
    const options = props?.selectProps?.options ?? [];
    const healthyCount = options.filter((o) => o?.value?.gateway_healthy)?.length ?? 0;
    const unhealthyCount = options.length - healthyCount;
    if (props?.selectProps?.menuIsOpen) {
        return React.createElement(components.ValueContainer, { ...props }, props.children);
    }
    return (React.createElement(components.ValueContainer, { ...props },
        selected && (React.createElement("div", { className: "flex items-center justify-between w-[95%]" },
            React.createElement("div", { className: "flex items-center mr-[18px]" },
                React.createElement(ColoredDot, { status: "isHealthy" in selected && selected.isHealthy ? "healthy" : "unhealthy", styleOverride: { marginTop: "-2px" } }),
                React.createElement("div", { className: styles.sandboxSelectorSingleValueLabel }, "label" in selected && selected.label)),
            React.createElement("div", { className: "flex items-center" },
                React.createElement("div", { className: "flex items-center mr-[8px]" },
                    React.createElement(ColoredDot, { status: "healthy", styleOverride: { marginTop: "-2px", marginRight: "4px" } }),
                    healthyCount),
                React.createElement("div", { className: "flex items-center" },
                    React.createElement(ColoredDot, { status: "unhealthy", styleOverride: { marginTop: "-2px", marginRight: "4px" } }),
                    unhealthyCount)))),
        React.Children.map(props.children, (child) => {
            // Filter out CustomSingleValue so there is no overlapping when menu is closed
            return React.isValidElement(child) && child?.type !== CustomSingleValue ? child : null;
        })));
};
export default OpenShellSandboxSelector;
